import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Chip,
  Tabs,
  Tab,
  Button,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TimerIcon from '@mui/icons-material/Timer';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useGame } from '../../contexts/GameContext';
import levelsData from '../../game/levels.json';
import PerformanceCharts from './PerformanceCharts';
import LevelDetailsGrid from './LevelDetailsGrid';

const ProgressDashboard = ({ onSelectLevel }) => {
  const { getStats, progress } = useGame();
  const stats = getStats();
  const [activeTab, setActiveTab] = useState(0);
  const containerRef = useRef(null);
  const statsGridRef = useRef(null);
  const statsCard0Ref = useRef(null);
  const recordsGridRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const log = () => {
      const c  = containerRef.current?.getBoundingClientRect();
      const sg = statsGridRef.current?.getBoundingClientRect();
      const s0 = statsCard0Ref.current?.getBoundingClientRect();
      const rg = recordsGridRef.current?.getBoundingClientRect();
      const sgCS  = statsGridRef.current  ? getComputedStyle(statsGridRef.current)  : null;
      const s0CS  = statsCard0Ref.current ? getComputedStyle(statsCard0Ref.current) : null;
      console.log('[WIDTH-DEBUG] ProgressDashboard');
      console.log('  container:', c?.width?.toFixed(1), '| window:', window.innerWidth);
      console.log('  statsGrid BBox:', sg?.width?.toFixed(1),
        'offsetW:', statsGridRef.current?.offsetWidth,
        'computed width:', sgCS?.width,
        'computed margin:', sgCS?.marginLeft, sgCS?.marginRight);
      console.log('  statsCard[0] BBox:', s0?.width?.toFixed(1),
        'offsetW:', statsCard0Ref.current?.offsetWidth,
        'computed width:', s0CS?.width,
        'computed padding:', s0CS?.paddingLeft, s0CS?.paddingRight,
        'computed flexBasis:', s0CS?.flexBasis);
      console.log('  recordsGrid BBox:', rg?.width?.toFixed(1));
      // Walk parent chain from containerRef up 6 levels
      let el = containerRef.current?.parentElement;
      for (let i = 0; i < 6 && el; i++) {
        const r = el.getBoundingClientRect();
        const cs = getComputedStyle(el);
        console.log(`  parent[${i}] tag=${el.tagName} class="${el.className?.substring?.(0,40)}" w=${r.width.toFixed(1)} overflowX=${cs.overflowX} maxW=${cs.maxWidth}`);
        el = el.parentElement;
      }
    };
    requestAnimationFrame(log);
    window.addEventListener('resize', log);
    return () => window.removeEventListener('resize', log);
  }, []);

  const totalLevels = levelsData.levels.length;
  const completionPercentage = Math.round((stats.totalLevelsCompleted / totalLevels) * 100);
  const maxStars = totalLevels * 5;
  const starPercentage = Math.round((stats.totalStars / maxStars) * 100);

  // Calculate XP (gamification metric for monetization)
  // Formula: stars * 100 + total score + streak bonus
  const calculateXP = () => {
    const starXP = stats.totalStars * 100;
    const scoreXP = Object.values(progress.highScores || {}).reduce((sum, score) => sum + score, 0);
    const streakBonus = stats.currentStreak * 50;
    return starXP + scoreXP + streakBonus;
  };

  const totalXP = calculateXP();

  // Calculate level (for progression system) - useful for future premium tiers
  const calculateLevel = (xp) => {
    // Level up every 1000 XP
    return Math.floor(xp / 1000) + 1;
  };

  const userLevel = calculateLevel(totalXP);
  const nextLevelXP = userLevel * 1000;
  const currentLevelXP = (userLevel - 1) * 1000;
  const xpProgress = ((totalXP - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;

  // Format play time
  const formatPlayTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // ── Empty state for brand-new users ──────────────────────────────────────────
  if (stats.totalLevelsCompleted === 0) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 6, width: '100%', boxSizing: 'border-box' }}>
        <Box textAlign="center">
          <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>🎵</Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: '#58CC02' }}>
            Your Journey Starts Here
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 340, mx: 'auto' }}>
            Complete your first level to unlock your progress dashboard — charts, streaks, achievements, and more!
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => onSelectLevel && onSelectLevel(levelsData.levels[0])}
            sx={{
              backgroundColor: '#58CC02',
              '&:hover': { backgroundColor: '#46a302' },
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: 8,
            }}
          >
            Play Level 1 ▶
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ px: { xs: 2, sm: 3 }, py: 4, width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#58CC02' }}>
          📊 Your Progress Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your journey to becoming a better singer
        </Typography>
      </Box>

      {/* XP & Level Card - Premium Feel */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <EmojiEventsIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Level {userLevel} Singer
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {totalXP.toLocaleString()} XP • {nextLevelXP - totalXP} XP to next level
                </Typography>
              </Box>
            </Box>
            <Box sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, height: 12 }}>
              <Box
                sx={{
                  width: `${xpProgress}%`,
                  bgcolor: '#ffd700',
                  height: '100%',
                  borderRadius: 2,
                  transition: 'width 0.5s ease',
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Chip
              icon={<LocalFireDepartmentIcon />}
              label={`${stats.currentStreak} day streak`}
              sx={{
                bgcolor: '#ff6b6b',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
                px: 1,
                py: 2,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Key Stats Grid */}
      <Grid ref={statsGridRef} container spacing={{ xs: 2, sm: 3 }} mb={4}>
        {/* Completion */}
        <Grid size={{ xs: 6, sm: 6, md: 3 }} ref={statsCard0Ref}>
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <Box sx={{ 
              width: { xs: 48, sm: 64 }, 
              height: { xs: 48, sm: 64 }, 
              borderRadius: '50%', 
              bgcolor: '#e3f2fd', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <TrendingUpIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#1976d2' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 0.5, fontSize: { xs: '1.3rem', sm: '2.125rem' } }}>
              {completionPercentage}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Course Completion
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.totalLevelsCompleted} / {totalLevels} levels
            </Typography>
          </Paper>
        </Grid>

        {/* Total Stars */}
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <Box sx={{ 
              width: { xs: 48, sm: 64 }, 
              height: { xs: 48, sm: 64 }, 
              borderRadius: '50%', 
              bgcolor: '#fff3e0', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <StarIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#f57c00' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 0.5, fontSize: { xs: '1.3rem', sm: '2.125rem' } }}>
              {stats.totalStars}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Stars
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {starPercentage}% of {maxStars} possible
            </Typography>
          </Paper>
        </Grid>

        {/* Average Score */}
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <Box sx={{ 
              width: { xs: 48, sm: 64 }, 
              height: { xs: 48, sm: 64 }, 
              borderRadius: '50%', 
              bgcolor: '#e8f5e9', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <AssessmentIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#388e3c' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#388e3c', mb: 0.5, fontSize: { xs: '1.3rem', sm: '2.125rem' } }}>
              {stats.averageScore}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Average Score
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Across {stats.totalLevelsCompleted} levels
            </Typography>
          </Paper>
        </Grid>

        {/* Play Time */}
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <Box sx={{ 
              width: { xs: 48, sm: 64 }, 
              height: { xs: 48, sm: 64 }, 
              borderRadius: '50%', 
              bgcolor: '#fce4ec', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <TimerIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#c2185b' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#c2185b', mb: 0.5, fontSize: { xs: '1.3rem', sm: '2.125rem' } }}>
              {formatPlayTime(stats.totalPlayTime)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Practice Time
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.totalAttempts} attempts
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Personal Bests */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          🏆 Personal Records
        </Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} ref={recordsGridRef}>
          <Grid size={{ xs: 4, sm: 4 }}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                Best Single Score
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#58CC02', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                {Math.max(...Object.values(progress.highScores || {}), 0)}%
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 4, sm: 4 }}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                Longest Streak
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ff6b6b', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                {stats.longestStreak} days
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 4, sm: 4 }}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                5-Star Levels
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ffd700', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                {Object.values(progress.levelStars || {}).filter(stars => stars === 5).length}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for different views */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tab label="📈 Performance Charts" />
          <Tab label="🎵 Level Details" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && <PerformanceCharts />}
        {activeTab === 1 && <LevelDetailsGrid onSelectLevel={onSelectLevel} />}
      </Box>
    </Box>
  );
};

export default ProgressDashboard;
